import { useState } from 'react'
import { Check, Palette, Sun, Moon, Monitor, Contrast } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useUiStore } from '@/app/store/ui'

type Theme = 'light' | 'dark' | 'enhanced-light' | 'enhanced-dark' | 'system'

const themeIconComponents: Record<Theme, React.ComponentType<{ className?: string }>> = {
    light: Sun,
    dark: Moon,
    'enhanced-light': Contrast,
    'enhanced-dark': Contrast,
    system: Monitor,
}

export function ThemeSwitcher() {
    const { t } = useTranslation()
    const { theme, setTheme } = useUiStore()
    const [open, setOpen] = useState(false)

    // Removed excessive logging that was causing console spam

    const themes: { value: Theme; label: string; description: string }[] = [
        { value: 'light', label: t('header.themes.light'), description: t('header.themeDescriptions.light') },
        { value: 'dark', label: t('header.themes.dark'), description: t('header.themeDescriptions.dark') },
        { value: 'enhanced-light', label: t('header.themes.enhancedLight'), description: t('header.themeDescriptions.enhancedLight') },
        { value: 'enhanced-dark', label: t('header.themes.enhancedDark'), description: t('header.themeDescriptions.enhancedDark') },
        { value: 'system', label: t('header.themes.system'), description: t('header.themeDescriptions.system') },
    ]

    const handleThemeChange = (newTheme: Theme) => {
        setTheme(newTheme)
        setOpen(false)
    }

    const CurrentIcon = themeIconComponents[theme] || Sun

    return (
        <div className="flex items-center gap-2">
            <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9"
                        title={t('header.changeTheme')}
                    >
                        <CurrentIcon className="h-4 w-4" />
                        <span className="sr-only">{t('header.changeTheme')}</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="flex items-center gap-2">
                        <Palette className="h-4 w-4" />
                        {t('header.chooseTheme')}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {themes.map((themeOption) => {
                        const Icon = themeIconComponents[themeOption.value]
                        const isSelected = theme === themeOption.value

                        return (
                            <DropdownMenuItem
                                key={themeOption.value}
                                onClick={() => handleThemeChange(themeOption.value)}
                                className="flex items-center gap-3 cursor-pointer"
                            >
                                <div className="flex items-center gap-2">
                                    <Icon className="h-4 w-4" />
                                    <div>
                                        <div className="font-medium">{themeOption.label}</div>
                                        <div className="text-xs text-muted-foreground">
                                            {themeOption.description}
                                        </div>
                                    </div>
                                </div>
                                {isSelected && (
                                    <Check className="h-4 w-4 ml-auto text-primary" />
                                )}
                            </DropdownMenuItem>
                        )
                    })}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}
